import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Tema visual da aplicacao.
 *
 * Os tokens ja trazem os tres temas (`packages/tokens/src/tokens.css`); o que
 * faltava era alguem escrever `data-theme` na raiz e a cor primaria escolhida.
 * E isso que este provider faz — do jeito do protótipo, que separa o **modo de
 * cor** (claro / escuro / sistema) do **tema nomeado** (a cor primaria).
 */

/** Modo de cor. `system` segue o SO via `prefers-color-scheme`. */
export type ColorMode = 'light' | 'dark' | 'system'

/**
 * MODELO do escuro (SPEC-20261103-2330): o Black e uma variante do modo
 * escuro, nao um quarto modo. `padrao` = o escuro azulado dos tokens;
 * `black` = o cinza neutro quase preto do Claude Desktop
 * (`styles/tema-black.css`). Vale no modo Escuro e, no Sistema, quando o SO
 * estiver escuro.
 */
export type DarkVariant = 'padrao' | 'black'

/** Temas do protótipo (`docs/design-slices/_shell.html:1952-1963`). */
export type ThemeDef = {
  name: string
  /** Cor primaria (semente da qual todos os tons sao derivados). */
  primary: string
  /** Alto contraste: forca o tema `hc` dos tokens. */
  highContrast?: boolean
  /** Amostra do circulo na grade de temas; por padrao, a propria primaria. */
  swatch?: string
  /** "Personalizado" abre o seletor de cor em vez de fixar uma primaria. */
  custom?: boolean
}

export const THEMES: readonly ThemeDef[] = [
  { name: 'Cobalto Orbind', primary: '#1B4DF5' },
  // Terracota do Claude: com o escuro no modelo Black fica igual ao Claude (SPEC-20261103-1932/2330)
  { name: 'Claude', primary: '#D97757', swatch: 'linear-gradient(135deg,#191919 50%,#D97757 50%)' },
  { name: 'Azul', primary: '#1D4ED8' },
  { name: 'Verde', primary: '#0F8A5F' },
  { name: 'Âmbar', primary: '#A66B0D' },
  { name: 'Grafite', primary: '#3F3350' },
  { name: 'Alto contraste', primary: '#1B4DF5', highContrast: true, swatch: '#05081A' },
  { name: 'Protanopia & Deuteranopia', primary: '#0F62FE', swatch: 'linear-gradient(135deg,#0F62FE,#3A2A5C)' },
  { name: 'Mint Chip', primary: '#0D9488', swatch: 'linear-gradient(135deg,#99F6E4,#0D9488)' },
  { name: 'Sunrise', primary: '#DB2777', swatch: 'linear-gradient(135deg,#FBCFE8,#DB2777)' },
  { name: 'Sea Glass', primary: '#7C3AED', swatch: 'linear-gradient(135deg,#BAE6FD,#7C3AED)' },
  { name: 'Personalizado', primary: '#1B4DF5', custom: true },
]

/** Tema de partida — o primeiro da lista do protótipo. */
const TEMA_PADRAO: ThemeDef = { name: 'Cobalto Orbind', primary: '#1B4DF5' }

type ThemeState = {
  mode: ColorMode
  setMode: (modo: ColorMode) => void
  /** Modelo do escuro (padrao | black). */
  darkVariant: DarkVariant
  setDarkVariant: (variante: DarkVariant) => void
  /** Nome do tema selecionado na grade. */
  theme: string
  setTheme: (nome: string) => void
  /** Primaria em uso — util para o seletor de cor do tema "Personalizado". */
  primary: string
  setPrimary: (cor: string) => void
}

const CHAVE_MODO = 'ob-color-mode'
const CHAVE_MODELO_ESCURO = 'ob-dark-variant'
const CHAVE_TEMA = 'ob-theme'
const CHAVE_PRIMARIA = 'ob-primary'

const Ctx = createContext<ThemeState | null>(null)

export function useTheme(): ThemeState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useTheme precisa do ThemeProvider')
  return ctx
}

/**
 * Le a preferencia guardada. Quem tinha o antigo modo "black" (quando ele era
 * um quarto modo) cai em Escuro + modelo Black, sem perder nada.
 */
function preferenciaGuardada(): { mode: ColorMode; darkVariant: DarkVariant } {
  const modo = localStorage.getItem(CHAVE_MODO)
  const modelo = localStorage.getItem(CHAVE_MODELO_ESCURO)
  if (modo === 'black') return { mode: 'dark', darkVariant: 'black' }
  return {
    mode: modo === 'light' || modo === 'dark' || modo === 'system' ? modo : 'system',
    darkVariant: modelo === 'black' ? 'black' : 'padrao',
  }
}

const CONSULTA_ESCURO = '(prefers-color-scheme: dark)'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [inicial] = useState(preferenciaGuardada)
  const [mode, setModeState] = useState<ColorMode>(inicial.mode)
  const [darkVariant, setDarkVariantState] = useState<DarkVariant>(inicial.darkVariant)
  const [theme, setThemeState] = useState(() => localStorage.getItem(CHAVE_TEMA) ?? TEMA_PADRAO.name)
  const [primary, setPrimaryState] = useState(() => localStorage.getItem(CHAVE_PRIMARIA) ?? TEMA_PADRAO.primary)
  // o SO esta escuro? So importa no modo Sistema com o modelo Black
  const [soEscuro, setSoEscuro] = useState(() => window.matchMedia(CONSULTA_ESCURO).matches)

  // Trocar de tema nomeado leva junto a primaria dele — menos no
  // "Personalizado", que preserva a cor que o usuario escolheu.
  const setTheme = useCallback((nome: string) => {
    setThemeState(nome)
    const def = THEMES.find((t) => t.name === nome)
    if (def && !def.custom) setPrimaryState(def.primary)
  }, [])

  const setMode = useCallback((modo: ColorMode) => setModeState(modo), [])
  const setDarkVariant = useCallback((variante: DarkVariant) => setDarkVariantState(variante), [])
  const setPrimary = useCallback((cor: string) => setPrimaryState(cor), [])

  useEffect(() => {
    const mq = window.matchMedia(CONSULTA_ESCURO)
    const aoMudar = (e: MediaQueryListEvent) => setSoEscuro(e.matches)
    mq.addEventListener('change', aoMudar)
    return () => mq.removeEventListener('change', aoMudar)
  }, [])

  useEffect(() => {
    const def = THEMES.find((t) => t.name === theme)
    const raiz = document.documentElement

    // Alto contraste vence o modo de cor: o tema `hc` ja e escuro.
    // No escuro, o modelo decide: `dark` (tokens) ou `black` (Claude). No
    // Sistema, o Black so entra quando o SO esta escuro; senao os tokens
    // resolvem `system` pela media query, como sempre.
    let tema: string = mode
    if (def?.highContrast) tema = 'hc'
    else if (mode === 'dark') tema = darkVariant === 'black' ? 'black' : 'dark'
    else if (mode === 'system' && darkVariant === 'black' && soEscuro) tema = 'black'
    raiz.dataset.theme = tema
    raiz.style.setProperty('--ob-primary-seed', primary)

    localStorage.setItem(CHAVE_MODO, mode)
    localStorage.setItem(CHAVE_MODELO_ESCURO, darkVariant)
    localStorage.setItem(CHAVE_TEMA, theme)
    localStorage.setItem(CHAVE_PRIMARIA, primary)
  }, [mode, darkVariant, soEscuro, theme, primary])

  const value = useMemo(
    () => ({ mode, setMode, darkVariant, setDarkVariant, theme, setTheme, primary, setPrimary }),
    [mode, setMode, darkVariant, setDarkVariant, theme, setTheme, primary, setPrimary],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
